import React, { Fragment, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import SkuflixSearchIcon from "core/icons/skuflix/SkuflixSearchIcon";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import Link from "arena/components/Link";
import { useViewer } from "core/components/ViewerProvider";
import { useArena } from "core/components/ArenaProvider";
import UserMenu from "./UserMenu";
import HeaderLogo from "./HeaderLogo";
import HeaderMenu from "./HeaderMenu";
import HeaderMenuPins from "./HeaderMenuPins";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import classNames from "classnames";
import { isTelegramMiniApp, launchParams } from "core/utils/telegram";
import OpenInWeb from "arena/components/OpenInWeb";
import { useMatch } from "react-router-dom";

// Хук для отслеживания направления скролла
function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = useState(null);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrolledPastThreshold, setScrolledPastThreshold] = useState(false);

  useEffect(() => {
    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset;
      const direction = scrollY > lastScrollY ? "down" : "up";

      // Проверяем порог скролла
      setScrolledPastThreshold(scrollY > 20);

      if (
        direction !== scrollDirection &&
        Math.abs(scrollY - lastScrollY) > 10
      ) {
        setScrollDirection(direction);
      }

      setLastScrollY(scrollY > 0 ? scrollY : 0);
    };

    window.addEventListener("scroll", updateScrollDirection);
    return () => window.removeEventListener("scroll", updateScrollDirection);
  }, [scrollDirection, lastScrollY]);

  return { scrollDirection, scrolledPastThreshold };
}

// Хук для определения открыт ли диалог с роутом
function useIsDialogOpen() {
  const videoMatch = useMatch("/video/:contentId");
  const shortyMatch = useMatch("/shorty/:contentId");
  const streamMatch = useMatch("/stream/:contentId");
  const tvMatch = useMatch("/tv/:slug");
  const channelMatch = useMatch("/channel/:contentId");
  const postMatch = useMatch("/post/:postId");
  const digitalAssetMatch = useMatch("/digitalAsset/:digitalAssetId");
  const bookMatch = useMatch("/book/:contentId");
  const productMatch = useMatch("/product/:productId");

  return !!(
    videoMatch ||
    shortyMatch ||
    streamMatch ||
    tvMatch ||
    channelMatch ||
    postMatch ||
    digitalAssetMatch ||
    bookMatch ||
    productMatch
  );
}

const PREFIX = "Header";

const classes = {
  avatarButton: `${PREFIX}-avatarButton`,
  avatar: `${PREFIX}-avatar`,
  menuButton: `${PREFIX}-menuButton`,
  title: `${PREFIX}-title`,
  search: `${PREFIX}-search`,
  signInDesktop: `${PREFIX}-signInDesktop`,
  signInMobile: `${PREFIX}-signInMobile`,
  buttonLabel: `${PREFIX}-buttonLabel`,
  searchIcon: `${PREFIX}-searchIcon`,
  arenaIcon: `${PREFIX}-arenaIcon`,
  appBar: `${PREFIX}-appBar`,
  appBarElevate: `${PREFIX}-appBarElevate`,
  rightMenu: `${PREFIX}-rightMenu`,
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  transition: "all ease 0.5s, transform 0.3s ease-in-out",
  zIndex: 1200, // Больше чем у Cover компонентов, но меньше чем у модальных диалогов (1300)
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.03)"
      : "rgba(0, 0, 0, 0.03)",
  backdropFilter: theme.palette.mode === "dark" ? "blur(5px)" : "blur(5px)",

  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: 100,
    pointerEvents: "none",
    transition: "all ease 1s",
    backgroundImage:
      theme.palette.mode === "dark"
        ? "linear-gradient(rgba(16,16,16,0.8), rgba(16,16,16,0.4), rgba(16,16,16,0.1), transparent)"
        : "linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.6),rgba(255,255,255,0.3), rgba(255,255,255,0.1), transparent)",
  },

  [`&.${classes.appBarElevate}`]: {
    backgroundColor: "rgba(255, 255, 255, 0)",
    boxShadow: "none",
  },

  "&.header-hidden": {
    transform: "translateY(-100%)",
  },

  [`& .${classes.avatarButton}`]: {
    padding: 9,
    [theme.breakpoints.up("sm")]: {
      padding: 4,
    },
  },

  [`& .${classes.avatar}`]: {
    width: 30,
    height: 30,
    borderRadius: 30,

    [theme.breakpoints.up("sm")]: {
      width: 40,
      height: 40,
      borderRadius: 40,
    },
  },

  [`& .${classes.menuButton}`]: {
    // marginRight: 10,

    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },

  [`& .${classes.title}`]: {
    marginLeft: 10,
  },

  [`& .${classes.search}`]: {
    flex: 1,
    margin: theme.spacing(0, 4),
    marginRight: "auto",
    // background: "#101010",
    maxWidth: 240,
    padding: "4px 10px",
    borderRadius: 4,
  },

  [`& .${classes.signInDesktop}`]: {
    display: "none",

    [theme.breakpoints.up("sm")]: {
      display: "flex",
    },
  },

  [`& .${classes.signInMobile}`]: {
    display: "flex",
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.buttonLabel}`]: {
    whiteSpace: "nowrap",
  },

  [`& .${classes.arenaIcon}`]: {
    [`@media (max-width: 350px)`]: {
      display: "none",
    },
  },

  // [`& .${classes.rightMenu}`]: {
  //   marginLeft: "auto",
  //   display: "flex",
  //   alignItems: "center",
  //   flexWrap: "nowrap",
  // },
}));

export default function Header({
  menuItems,
  onClickMenuMobile,
  onClickSignIn,
  onClickSearch,
  onClickMenuItem,
}) {
  const { t } = useTranslation("arena");
  const viewer = useViewer();
  const arena = useArena();
  const { scrollDirection, scrolledPastThreshold } = useScrollDirection();
  const isDialogOpen = useIsDialogOpen();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  const isDesktopMiniApp =
    isTelegramMiniApp && !["android", "ios"].includes(launchParams?.platform);

  // Определяем, должен ли хедер быть скрыт
  // Не скрываем хедер, если открыт диалог
  const shouldHideHeader =
    arena?.hideableHeader &&
    scrollDirection === "down" &&
    scrolledPastThreshold &&
    !isDialogOpen;

  return (
    <StyledAppBar
      color="default"
      position="fixed"
      className={classNames({
        [classes.appBarElevate]: !trigger,
        "header-hidden": shouldHideHeader,
      })}
    >
      <Toolbar
        disableGutters
        sx={{
          pl: { xs: 0, sm: 3 },
          pr: { xs: 0, sm: 3 },
          minHeight: { xs: 48, sm: 64 },
        }}
      >
        <Tooltip title={t("header.menu")}>
          <IconButton
            onClick={onClickMenuMobile}
            className={classes.menuButton}
            size="medium"
            sx={{ mr: { xs: 0, sm: 0.5 }, ml: { xs: -0.5, sm: 0 } }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            flex: { xs: 1, md: "0 0 auto" },
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            overflow: { xs: "hidden", md: "visible" },
            mr: { xs: 0.5, md: 0 },
          }}
        >
          <HeaderLogo arena={arena} />
        </Box>

        <HeaderMenu arena={arena} onClickMenuItem={onClickMenuItem} />

        <Stack
          direction="row"
          alignItems="center"
          sx={{
            marginLeft: "auto",
            flexShrink: 0,
          }}
        >
          {isDesktopMiniApp && <OpenInWeb />}

          <HeaderMenuPins menuPinItems={arena.menuPinItems} />

          <Box
            sx={{
              ml: { xs: 1, md: 2.5 },
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Tooltip title={t("header.search")}>
              <IconButton
                component={Link}
                to="/search"
                onClick={onClickSearch}
                size="small"
                sx={{ width: 24, height: 24, p: 0 }}
              >
                <SkuflixSearchIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {typeof viewer !== "undefined" && arena.isRegistrationEnabled && (
            <Box
              sx={{
                ml: { xs: 0.5, sm: 2 },
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {viewer ? (
                <UserMenu viewer={viewer} menuItems={menuItems} arena={arena} />
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={onClickSignIn}
                    className={classes.signInDesktop + " js-sign-in"}
                    classes={{
                      label: classes.buttonLabel,
                    }}
                  >
                    {t("header.signIn")}
                  </Button>

                  <IconButton
                    onClick={onClickSignIn}
                    className={classes.signInMobile + " js-sign-in"}
                    edge="end"
                    size="medium"
                    sx={{ mr: { xs: -0.5, sm: 0 } }}
                  >
                    <AccountCircleIcon />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Stack>
      </Toolbar>
    </StyledAppBar>
  );
}

Header.propTypes = {
  className: PropTypes.string,
  onClickMenuItem: PropTypes.func,
  onClickSearch: PropTypes.func,
  onClickMenuMobile: PropTypes.func,
  onClickSignIn: PropTypes.func,
  menuItems: PropTypes.array,
};
